import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  TrendingUp, Sparkles, Send, ArrowRight, FileText, Calendar, Upload,
  Search, Mic2, DollarSign, Target, Activity as ActivityIcon
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

const STATUS_COLORS = {
  Applied: "#a78bfa",
  "In Review": "#f59e0b",
  Interview: "#22d3ee",
  Offer: "#10b981",
  Rejected: "#ef4444",
};

function Donut({ value, label, color = "#10b981" }) {
  const r = 36;
  const c = 2 * Math.PI * r;
  const off = c - (value / 100) * c;
  return (
    <div className="relative w-24 h-24">
      <svg className="w-24 h-24 -rotate-90">
        <circle cx="48" cy="48" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="8" fill="none" />
        <circle cx="48" cy="48" r={r} stroke={color} strokeWidth="8" fill="none"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <div>
          <div className="text-xl font-bold text-white">{value}%</div>
        </div>
      </div>
    </div>
  );
}

function Stat({ title, value, sub, accent = "text-violet-300", children, testid }) {
  return (
    <div className="jr-card" data-testid={testid}>
      <div className="text-xs text-zinc-400 mb-2">{title}</div>
      <div className="flex items-center gap-3">
        {children}
        <div>
          <div className={`text-3xl font-bold text-white`}>{value}</div>
          {sub && <div className={`text-xs ${accent} mt-1`}>{sub}</div>}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState([]);
  const [activities, setActivities] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [matches, setMatches] = useState([]);
  const [copilotMsg, setCopilotMsg] = useState("");
  const [copilotHistory, setCopilotHistory] = useState([]);
  const [copilotLoading, setCopilotLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}),
      api.get("/dashboard/trends?days=14").then((r) => setTrend(r.data)).catch(() => {}),
      api.get("/dashboard/activities?limit=5").then((r) => setActivities(r.data)).catch(() => {}),
      api.get("/dashboard/upcoming-interviews").then((r) => setInterviews(r.data)).catch(() => {}),
      api.get("/jobs/matches?limit=3").then((r) => setMatches(r.data)).catch(() => {}),
    ]);
  }, []);

  const askCopilot = async (q) => {
    const message = q ?? copilotMsg;
    if (!message.trim()) return;
    setCopilotHistory((h) => [...h, { role: "user", text: message }]);
    setCopilotMsg("");
    setCopilotLoading(true);
    try {
      const res = await api.post("/copilot/chat", { message });
      setCopilotHistory((h) => [...h, { role: "assistant", text: res.data.reply }]);
    } catch (e) {
      setCopilotHistory((h) => [...h, { role: "assistant", text: "Sorry, I couldn't reach the AI right now." }]);
    } finally {
      setCopilotLoading(false);
    }
  };

  const breakdown = stats?.applications?.breakdown || {};
  const pieData = ["Applied", "In Review", "Interview", "Offer", "Rejected"].map((s) => ({
    name: s,
    value: breakdown[s] || 0,
  }));
  const totalApps = pieData.reduce((a, b) => a + b.value, 0);
  const trendData = trend.map((t) => ({ ...t, label: t.date.slice(5) }));

  return (
    <div className="space-y-6">
      {/* Top stat row */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="stats-grid">
        <Stat title="Profile Strength" value={`${stats?.profile_strength ?? 0}%`} sub={stats?.profile_strength_verdict || ""} accent="text-emerald-300" testid="stat-profile-strength">
          <Donut value={stats?.profile_strength ?? 0} color="#10b981" />
        </Stat>
        <Stat title="Job Matches" value={stats?.job_matches?.new ?? 0} sub={stats?.job_matches?.trend || ""} accent="text-emerald-300" testid="stat-job-matches">
          <TrendingUp className="w-8 h-8 text-violet-300" />
        </Stat>
        <Stat title="Applications" value={stats?.applications?.total ?? 0} sub={`${stats?.applications?.in_progress ?? 0} in progress`} testid="stat-applications">
          <Send className="w-8 h-8 text-cyan-300" />
        </Stat>
        <Stat title="Interviews" value={stats?.interviews?.upcoming ?? 0} sub={`Next: ${stats?.interviews?.next || "None scheduled"}`} accent="text-cyan-300" testid="stat-interviews">
          <Mic2 className="w-8 h-8 text-cyan-300" />
        </Stat>
        <Stat title="Resume Score" value={stats?.resume_score?.score != null ? `${stats.resume_score.score}/100` : "—"} sub={stats?.resume_score?.verdict || ""} accent="text-emerald-300" testid="stat-resume-score">
          <Sparkles className="w-8 h-8 text-emerald-300" />
        </Stat>
      </div>

      {/* Upload + Copilot */}
      <div className="grid lg:grid-cols-2 gap-6">
        <UploadCard />
        <CopilotCard
          msg={copilotMsg}
          setMsg={setCopilotMsg}
          history={copilotHistory}
          ask={askCopilot}
          loading={copilotLoading}
        />
      </div>

      {/* Trends + Pie */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="jr-card" data-testid="trends-card">
          <div className="flex justify-between mb-4">
            <div className="font-semibold text-white">Job Match Trends</div>
            <div className="text-xs text-zinc-400 px-3 py-1 rounded-md bg-white/5 border border-white/10">Last 14 Days</div>
          </div>
          <div className="h-56" style={{ minHeight: 224, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#8b5cf6" />
                    <stop offset="100%" stopColor="#22d3ee" />
                  </linearGradient>
                </defs>
                <XAxis dataKey="label" stroke="#6b7280" fontSize={11} />
                <YAxis stroke="#6b7280" fontSize={11} />
                <Tooltip contentStyle={{ background: "#161635", border: "1px solid rgba(139,92,246,0.3)", borderRadius: 8 }} />
                <Line type="monotone" dataKey="count" stroke="url(#grad)" strokeWidth={2.5} dot={{ fill: "#8b5cf6", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="jr-card" data-testid="applications-overview-card">
          <div className="font-semibold text-white mb-4">Applications Overview</div>
        <div className="flex items-center gap-6 flex-col sm:flex-row" style={{ minWidth: 0 }}>
            <div className="relative">
              <ResponsiveContainer width={180} height={180}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" innerRadius={50} outerRadius={75} paddingAngle={2}>
                    {pieData.map((e) => <Cell key={e.name} fill={STATUS_COLORS[e.name]} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{totalApps}</div>
                  <div className="text-[10px] text-zinc-400 tracking-widest">TOTAL</div>
                </div>
              </div>
            </div>
            <div className="flex-1 space-y-2">
              {pieData.map((p) => {
                const pct = totalApps ? ((p.value / totalApps) * 100).toFixed(1) : "0.0";
                return (
                  <div key={p.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[p.name] }} />
                      <span className="text-zinc-300">{p.name}</span>
                    </div>
                    <div className="text-zinc-400 text-xs">{p.value} ({pct}%)</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Activity + Interviews */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="jr-card" data-testid="recent-activity-card">
          <div className="font-semibold text-white mb-4">Recent Activity</div>
          <div className="space-y-3">
            {activities.length === 0 && (
              <div className="text-sm text-zinc-500 py-6 text-center" data-testid="no-activity-msg">
                No activity yet. Search jobs or upload a resume to get started.
              </div>
            )}
            {activities.slice(0, 5).map((a, i) => (
              <div key={a.created_at || i} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-violet-500/15 grid place-items-center text-violet-300">
                  <ActivityIcon className="w-4 h-4" />
                </div>
                <div className="flex-1 text-sm text-zinc-300">{a.text}</div>
                <div className="text-xs text-zinc-500 shrink-0">{a.created_at ? timeAgo(a.created_at) : ""}</div>
              </div>
            ))}
          </div>
          <Link to="/applications" className="mt-4 block text-center jr-btn-ghost text-sm py-2">View All Activity</Link>
        </div>

        <div className="jr-card" data-testid="upcoming-interviews-card">
          <div className="font-semibold text-white mb-4">Upcoming Interviews</div>
          <div className="space-y-3">
            {interviews.length === 0 && (
              <div className="text-sm text-zinc-500 py-6 text-center" data-testid="no-interviews-msg">
                No interviews scheduled yet. Mark an application as "Interview" to see it here.
              </div>
            )}
            {interviews.slice(0, 3).map((iv, i) => (
              <div key={iv.application_id || `${iv.company}-${iv.date}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">
                <div className="w-9 h-9 rounded-lg bg-cyan-500/15 grid place-items-center text-cyan-300">
                  <Calendar className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-white">{iv.company}</div>
                  <div className="text-xs text-zinc-400">{iv.role || iv.job_title}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-zinc-300">{(iv.date || "").slice(0, 10)}</div>
                  <div className="text-[10px] text-zinc-500">{iv.time || ""}</div>
                </div>
              </div>
            ))}
          </div>
          <Link to="/calendar" className="mt-4 block text-center jr-btn-ghost text-sm py-2">View Calendar</Link>
        </div>
      </div>

      {/* Top matches + Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="jr-card" data-testid="top-matches-card">
          <div className="flex justify-between mb-4">
            <div className="font-semibold text-white">Top Job Matches For You</div>
            <Link to="/job-matches" className="text-xs text-violet-300">View All</Link>
          </div>
          <div className="space-y-3">
            {matches.length === 0 && (
              <div className="text-sm text-zinc-500 py-6 text-center" data-testid="no-matches-msg">
                No matches yet. Run a job search to find roles that fit your profile.
              </div>
            )}
            {matches.slice(0, 3).map((j, i) => (
              <div key={j.job_id || `${j.company}-${j.title}`} className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition">
                <div className="w-10 h-10 rounded-lg bg-white/10 grid place-items-center text-zinc-200 font-bold text-xs shrink-0">
                  {(j.company || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white truncate">{j.title}</div>
                  <div className="text-xs text-zinc-400 truncate">{j.company} • {j.location}</div>
                </div>
                <span className="jr-pill shrink-0 whitespace-nowrap" style={{ background: "rgba(16,185,129,0.15)", color: "#6ee7b7" }}>{j.match_score || 90}% Match</span>
                <span className="jr-pill shrink-0 whitespace-nowrap !hidden sm:!inline-flex" style={{ background: "rgba(139,92,246,0.15)", color: "#c4b5fd" }}>{j.remote ? "Remote" : "Onsite"}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="jr-card" data-testid="quick-actions-card">
          <div className="font-semibold text-white mb-4">Quick Actions</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { i: Search, t: "Search Jobs", s: "Find relevant jobs", to: "/job-search", c: "text-emerald-300", testid: "qa-search" },
              { i: Sparkles, t: "Optimize Resume", s: "Improve ATS score", to: "/ats-optimizer", c: "text-violet-300", testid: "qa-ats" },
              { i: FileText, t: "Cover Letter", s: "AI-powered letters", to: "/cover-letter", c: "text-pink-300", testid: "qa-cover" },
              { i: Mic2, t: "Interview Prep", s: "Practice & improve", to: "/interview-prep", c: "text-cyan-300", testid: "qa-interview" },
              { i: DollarSign, t: "Salary Insights", s: "Know your worth", to: "/salary-insights", c: "text-amber-300", testid: "qa-salary" },
              { i: Target, t: "Track Applications", s: "Stay organized", to: "/applications", c: "text-rose-300", testid: "qa-track" },
            ].map((a) => (
              <Link key={a.t} to={a.to} data-testid={a.testid}
                className="jr-card p-4 text-center hover:translate-y-[-2px] transition-all">
                <a.i className={`w-6 h-6 mx-auto mb-2 ${a.c}`} />
                <div className="text-xs font-semibold text-white">{a.t}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">{a.s}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function UploadCard() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upload = async (f) => {
    if (!f) return;
    setLoading(true);
    setDone(false);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/resume/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="jr-card" data-testid="upload-resume-card" style={{
      background: "linear-gradient(135deg, rgba(139,92,246,0.18), rgba(34,211,238,0.08))"
    }}>
      <div className="font-bold text-xl text-white mb-1">Upload Your Resume</div>
      <p className="text-sm text-zinc-400 mb-4">Upload your latest resume to get AI-powered insights, better matches and tailored suggestions.</p>

      <label className="block border-2 border-dashed border-violet-500/30 rounded-xl p-8 text-center cursor-pointer hover:bg-white/[0.03] transition">
        <Upload className="w-8 h-8 text-violet-300 mx-auto mb-2" />
        <div className="text-sm text-zinc-300">
          {loading ? "Uploading & parsing..." : done ? "Resume uploaded ✓" : "Drag & drop your file here"}
        </div>
        <div className="text-xs text-zinc-500 mt-1">PDF, DOCX (Max 10MB)</div>
        <input
          data-testid="resume-file-input"
          type="file"
          accept=".pdf,.docx,.txt"
          className="hidden"
          onChange={(e) => { setFile(e.target.files[0]); upload(e.target.files[0]); }}
        />
        <div className="mt-3 inline-block jr-btn-primary text-sm">{loading ? "Working..." : "Browse Files"}</div>
      </label>

      <p className="text-xs text-zinc-500 mt-3">Your resume is secure and only used to improve your experience.</p>
      <Link to="/upload-resume" className="text-xs text-violet-300 inline-flex items-center gap-1 mt-2">
        Tips to improve your resume <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

function CopilotCard({ msg, setMsg, history, ask, loading }) {
  const suggestions = [
    "What are the best job roles for my profile?",
    "Improve my resume for a Data Scientist role",
    "Show me top remote jobs in Pune",
    "How can I prepare for a System Design interview?",
  ];
  return (
    <div className="jr-card relative overflow-hidden" data-testid="copilot-card">
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-300" />
            <div className="font-bold text-xl text-white">AI Career Copilot</div>
            <span className="jr-pill bg-violet-500/20 text-violet-200 text-[10px]">BETA</span>
          </div>
          <p className="text-sm text-zinc-400 mt-1 max-w-md">Ask me anything about your career, jobs, resume or interviews.</p>
        </div>
      </div>

      <div className="mt-4 space-y-2 max-h-56 overflow-y-auto">
        {history.length === 0 ? suggestions.map((s, i) => (
          <button key={s} onClick={() => ask(s)} data-testid={`copilot-suggestion-${i}`}
            className="block w-full text-left text-sm text-zinc-300 px-3 py-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.07] border border-white/5">
            {s}
          </button>
        )) : history.map((m, i) => (
          <div key={`msg-${i}-${m.role}`} className={`text-sm p-2.5 rounded-lg ${m.role === "user" ? "bg-violet-500/15 text-violet-100" : "bg-white/5 text-zinc-200"}`}>
            <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">{m.role}</div>
            <div className="whitespace-pre-wrap">{m.text}</div>
          </div>
        ))}
        {loading && <div className="text-sm text-zinc-400 jr-pulse">Thinking…</div>}
      </div>

      <form className="mt-4 flex gap-2" onSubmit={(e) => { e.preventDefault(); ask(); }}>
        <input
          data-testid="copilot-input"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          placeholder="Ask anything..."
          className="jr-input"
        />
        <button type="submit" className="jr-btn-primary" data-testid="copilot-send" disabled={loading}>
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
}
